import { Component, computed, effect, ElementRef, inject, signal, Signal, TemplateRef, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Reservation, StatusForAgentEnum, TouristDocumentDto } from '@appTypes/api.types';
import { ConversationReferenceTypeEnum } from '@appTypes/chat-api.types';
import { maxLengthWithMessage } from '@core/misc/validators.utils';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgxsFormDirective, UpdateFormValue } from '@ngxs/form-plugin';
import { Actions, createDispatchMap, createSelectMap, ofActionSuccessful, Store } from '@ngxs/store';
import {
  buildReservationConversationAttributes,
  NGXS_RESERVATION_TOURIST_DOCUMENTS_UPLOAD_FORM,
  RES_MAXIMUM_ATTACHMENTS_SIZE_MB,
  RES_TOURIST_DOCUMENT_TITLE_MAX_LENGTH,
  RES_TOURIST_DOCUMENT_TITLE_TOO_LONG_MESSAGE,
  RES_TOURIST_DOCUMENT_UPLOAD_ALLOWED_STATUS_MESSAGE,
  RES_TOURIST_DOCUMENT_UPLOADED_CHAT_MESSAGE,
  RES_TOURIST_DOCUMENTS_UPLOAD_FILE_TYPES
} from '@pages/reservations-page/reservations.const';
import { ResTouristDocsUploadFormData } from '@pages/reservations-page/reservations.type';
import { reservationTouristFullName } from '@pages/reservations-page/reservations.utils';
import {
  reservationDeleteTouristDocument,
  reservationDownloadTouristDocument,
  reservationGetTouristDocuments,
  reservationUploadTouristDocument
} from '@pages/reservations-page/state/reservations.action';
import { ReservationsState } from '@pages/reservations-page/state/reservations.state';
import { MessageBoxComponent } from '@shared/components/message-box/message-box.component';
import { ButtonLoadingDirective } from '@shared/directives/button-loading.directive';
import { KzInputDirective } from '@shared/directives/kz-input.directive';
import { chatSendMessageByReference } from '@shared/modules/chat/state/chat.actions';
import { ConfirmService } from '@shared/modules/confirm/confirm.service';
import { DynamicModalData, OnDynamicModal } from '@shared/modules/dynamic-modal/dynamic-modal.types';

/**
 * The per-tourist **Upload Documents** popup of a reservation row: the tourist's uploaded
 * documents (view / delete) and the form to add one.
 *
 * Uploading is allowed only while the reservation is `New` (KM-5604). The popup reads the
 * reservation live from the search results, so it blocks uploads — with the status warning — the
 * moment the status there is not `New`. The API re-checks NugiosCore on every upload; a refusal
 * from there re-reads the reservation and reports through the error dialog (see
 * `reservationUploadTouristDocument`), after which the warning explains the block. Should the
 * re-read still say `New`, the refusal itself is shown inline and blocks uploads until the popup
 * is reopened.
 *
 * The title is capped at {@link RES_TOURIST_DOCUMENT_TITLE_MAX_LENGTH} characters, as it becomes the
 * Event title in NugiosCore, and the chat notice of an upload names the tourist (KM-5638).
 */
@Component({
  selector: 'app-res-tourist-docs-upload-form',
  imports: [
    KzInputDirective,
    TranslatePipe,
    MatButton,
    ReactiveFormsModule,
    ButtonLoadingDirective,
    NgxsFormDirective,
    MatIcon,
    MatProgressSpinner,
    MatProgressBar,
    MessageBoxComponent
  ],
  templateUrl: './res-tourist-docs-upload-form.component.html'
})
export class ResTouristDocsUploadFormComponent implements OnDynamicModal<ResTouristDocsUploadFormData> {
  // #region Injections & State
  readonly #store = inject(Store);
  readonly #translateService = inject(TranslateService);
  readonly #confirmService = inject(ConfirmService);
  readonly #actionsStream = inject(Actions);

  readonly #selectors = createSelectMap({
    uploadState: ReservationsState.touristDocumentUploadState,
    touristDocuments: ReservationsState.touristDocuments,
    documentsLoadState: ReservationsState.touristdocumentsLoadState,
    downloadState: ReservationsState.touristDocumentDownloadState,
    deleteState: ReservationsState.touristDocumentDeleteState
  });

  readonly #actions = createDispatchMap({
    uploadTouristDocument: reservationUploadTouristDocument,
    getTouristDocuments: reservationGetTouristDocuments,
    downloadDocument: reservationDownloadTouristDocument,
    deleteDocument: reservationDeleteTouristDocument,
    updateFormValue: UpdateFormValue,
    sendMessageByReference: chatSendMessageByReference
  });

  readonly #modalData = signal<ResTouristDocsUploadFormData | null>(null);

  /** The store's view of the reservation the popup was opened for — set once the modal hands its id over. */
  readonly #reservationSource = signal<Signal<Reservation | null> | null>(null);

  // #endregion

  // #region Public Properties

  public readonly fileInputRef = viewChild.required<ElementRef<HTMLInputElement>>('fileInputRef');
  public readonly modalFooterTemplateRef = viewChild.required<TemplateRef<unknown>>('modalFooterTemplateRef');

  public readonly ngxsForm = NGXS_RESERVATION_TOURIST_DOCUMENTS_UPLOAD_FORM;
  public readonly acceptedFilesTypes = RES_TOURIST_DOCUMENTS_UPLOAD_FILE_TYPES;
  public readonly uploadAllowedStatusMessage = RES_TOURIST_DOCUMENT_UPLOAD_ALLOWED_STATUS_MESSAGE;

  public readonly formGroup = new FormGroup({
    file: new FormControl<File | null>(null, Validators.required),
    title: new FormControl<string | null>(null, [
      Validators.required,
      // the ticket's own wording, in place of the generic length message
      maxLengthWithMessage(RES_TOURIST_DOCUMENT_TITLE_MAX_LENGTH, {
        text: RES_TOURIST_DOCUMENT_TITLE_TOO_LONG_MESSAGE,
        data: { maxLength: RES_TOURIST_DOCUMENT_TITLE_MAX_LENGTH }
      })
    ]),
    touristId: new FormControl<string | null>(null),
    rfid: new FormControl<string | null>(null)
  });

  readonly #formStatus = toSignal(this.formGroup.statusChanges, { initialValue: this.formGroup.status });

  /** Whether Upload was pressed at least once — validation messages show only from then on. */
  public readonly isSubmitted = signal(false);

  /**
   * The reservation the popup was opened for, as the search results hold it right now — `null`
   * until the modal hands its id over, or once it is no longer in the results.
   */
  public readonly reservation = computed<Reservation | null>(() => this.#reservationSource()?.() ?? null);

  /** Whether the reservation is `New` — the only status uploads are allowed for. */
  public readonly isReservationNew = computed<boolean>(
    () => this.reservation()?.statusForAgent === StatusForAgentEnum.New
  );

  /** The tourist's uploaded documents. */
  public readonly documents = computed<TouristDocumentDto[]>(() => this.#selectors.touristDocuments().documents);

  /** `true` while the documents list is being (re)loaded. */
  public readonly isDocumentsLoading = computed<boolean>(
    () => this.#selectors.documentsLoadState().status === 'loading'
  );

  /** `true` while any document download is in flight — every View is disabled meanwhile. */
  public readonly isDownloading = computed<boolean>(() => this.#selectors.downloadState().status === 'loading');

  /** `fileId` of the document being downloaded right now, so only its View shows the spinner. */
  public readonly downloadingFileId = computed<string | null>(() => {
    const loadState = this.#selectors.downloadState();
    return loadState.status === 'loading' ? (loadState.additionlData?.fileId ?? null) : null;
  });

  /** `true` while a document deletion is in flight. */
  public readonly isDeleting = computed<boolean>(() => this.#selectors.deleteState().status === 'loading');

  /** `fileId` of the document being deleted right now, so only its Delete shows the spinner. */
  public readonly deletingFileId = computed<string | null>(() => {
    const loadState = this.#selectors.deleteState();
    return loadState.status === 'loading' ? (loadState.additionlData?.fileId ?? null) : null;
  });

  /** `true` while the upload is in flight. */
  public readonly isUploading = computed<boolean>(() => this.#selectors.uploadState().status === 'loading');

  /**
   * The API's refusal of the last upload, shown inline, or `null`. It is only needed while the
   * store still holds the reservation as `New` — once the re-read moved it on, the status warning
   * explains the block by itself.
   */
  public readonly uploadRefusalMessage = computed<string | null>(() => {
    const uploadState = this.#selectors.uploadState();
    if (uploadState.status !== 'error' || !uploadState.errorMessage) return null;
    return this.isReservationNew() ? uploadState.errorMessage : null;
  });

  /** Whether an upload may be attempted at all: the reservation is `New` and the API has not refused one. */
  public readonly isUploadAllowed = computed<boolean>(
    () => this.isReservationNew() && this.uploadRefusalMessage() === null
  );

  /** Upload is disabled while uploads are not allowed, and after a submit attempt while the form is invalid. */
  public readonly isUploadDisabled = computed<boolean>(
    () => !this.isUploadAllowed() || (this.isSubmitted() && this.#formStatus() === 'INVALID')
  );

  /** Assigned by the dynamic modal; closes the popup. */
  public onDynamicModalClose?: () => void;

  // #endregion

  constructor() {
    this.#actionsStream
      .pipe(ofActionSuccessful(reservationUploadTouristDocument), takeUntilDestroyed())
      .subscribe(() => this.#onUploaded());

    // the reactive form is imperative — keep its controls in step with whether uploads are allowed
    effect(() => this.#setFormEnabled(this.isUploadAllowed()));
  }

  // #region Public Methods

  /**
   * Receives the tourist and the reservation id from the dynamic modal, binds the popup to that
   * reservation in the store and loads the tourist's documents.
   */
  public onDynamicModal(data: DynamicModalData<ResTouristDocsUploadFormData>): void {
    this.#modalData.set(data);
    this.#reservationSource.set(this.#store.selectSignal(ReservationsState.reservation(data.reservationId)));
    this.#patchFormIds();
    if (data.tourist.id) this.#actions.getTouristDocuments(data.tourist.id, data.reservationId);
  }

  /** Takes the chosen file into the form, or rejects it with a warning when it exceeds the size limit. */
  public onFileSelected(): void {
    const input = this.fileInputRef().nativeElement;
    const file = input.files?.[0];
    if (!file) return;

    const maxFileSize = RES_MAXIMUM_ATTACHMENTS_SIZE_MB * 1024 * 1024;
    if (file.size > maxFileSize) {
      this.#confirmService.confirm(
        {
          title: 'Maximum file size exceeded',
          message: this.#translateService.instant('Maximum size of file to attach is {maxSize} MB', {
            maxSize: RES_MAXIMUM_ATTACHMENTS_SIZE_MB
          })
        },
        'warn'
      );
      input.value = '';
      this.formGroup.controls.file.setValue(null);
      return;
    }
    this.formGroup.controls.file.setValue(file);
  }

  /** Downloads one of the tourist's documents. */
  public onDownloadDocument(document: TouristDocumentDto): void {
    const data = this.#modalData();
    if (!data?.tourist.id) return;
    this.#actions.downloadDocument(document, data.tourist.id, data.reservationId);
  }

  /** Deletes one of the tourist's documents. */
  public onDeleteDocument(document: TouristDocumentDto): void {
    const data = this.#modalData();
    if (!data?.tourist.id) return;
    this.#actions.deleteDocument(document, data.tourist.id, data.reservationId);
  }

  /** Uploads the chosen file once the form is valid; the first press only reveals the validation messages. */
  public onSubmit(): void {
    if (!this.isUploadAllowed()) return;
    this.isSubmitted.set(true);
    if (this.formGroup.invalid) return;

    // `[ngxsForm]` syncs on `valueChanges` only — pushing the raw value keeps the `File` in the store
    this.#actions.updateFormValue({ path: this.ngxsForm, value: this.formGroup.getRawValue() });
    this.#actions.uploadTouristDocument();
  }

  // #endregion

  // #region Private Methods

  /**
   * Clears the form after a successful upload and lets the reservation chat know about the file,
   * naming the tourist it was uploaded for.
   */
  #onUploaded(): void {
    if (this.#selectors.uploadState().status !== 'completed') return;
    this.#resetForm();

    const reservation = this.reservation();
    const tourist = this.#modalData()?.tourist;
    if (!reservation?.number || !tourist) return;
    this.#actions.sendMessageByReference({
      reference: ConversationReferenceTypeEnum.RESERVATION,
      referenceId: reservation.number,
      content: this.#translateService.instant(RES_TOURIST_DOCUMENT_UPLOADED_CHAT_MESSAGE, {
        touristName: reservationTouristFullName(tourist)
      }),
      attributes: buildReservationConversationAttributes(reservation)
    });
  }

  /** Locks or frees the title and file controls; the ids stay untouched either way. */
  #setFormEnabled(isEnabled: boolean): void {
    const { title, file } = this.formGroup.controls;
    if (isEnabled) {
      title.enable();
      file.enable();
    } else {
      title.disable();
      file.disable();
    }
  }

  #patchFormIds(): void {
    const data = this.#modalData();
    if (!data?.tourist.id) return;
    this.formGroup.controls.touristId.setValue(data.tourist.id);
    this.formGroup.controls.rfid.setValue(data.reservationId);
  }

  #resetForm(): void {
    this.isSubmitted.set(false);
    this.formGroup.reset();
    this.#patchFormIds();
    this.fileInputRef().nativeElement.value = '';
    this.#actions.updateFormValue({ path: this.ngxsForm, value: this.formGroup.getRawValue() });
  }

  // #endregion
}
